// ── MenuProfitabilityRanking — 메뉴 이익률 랭킹 위젯 ──
// Top 5 (효자 메뉴) + Bottom 3 (요주의 메뉴) 표시
import React, { useState, useMemo } from 'react';
import {
  calcMenuProfitability, summarizeMenuProfitability,
  aggregateMenuSales, enrichWithActualSales,
} from '../utils/menuProfitability';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function MenuProfitabilityRanking({
  categories, prices, priceAverages,
  transactions = [], monthKey,
  isMobile, onNavigate,
}) {
  const [sortMode, setSortMode] = useState('margin');  // 'margin' | 'profit' | 'actual'

  const baseMenus = useMemo(
    () => calcMenuProfitability({ categories, prices, priceAverages }),
    [categories, prices, priceAverages]
  );

  // 메뉴 판매 집계 (이번달 transactions의 lineItems에서)
  const salesAgg = useMemo(() => {
    if (!monthKey) return {};
    return aggregateMenuSales(transactions, { start: `${monthKey}-01`, end: `${monthKey}-31` });
  }, [transactions, monthKey]);

  const hasActualSales = Object.keys(salesAgg).length > 0;
  const menus = useMemo(
    () => hasActualSales ? enrichWithActualSales(baseMenus, salesAgg) : baseMenus,
    [baseMenus, salesAgg, hasActualSales]
  );
  const summary = useMemo(() => summarizeMenuProfitability(menus), [menus]);

  // 평가 가능한 메뉴
  const evaluable = useMemo(
    () => menus.filter(m => m.hasPrice && m.hasIngredients && m.marginPct !== null),
    [menus]
  );

  const sorted = useMemo(() => {
    const arr = [...evaluable];
    if (sortMode === 'margin') return arr.sort((a, b) => b.marginPct - a.marginPct);
    if (sortMode === 'actual' && hasActualSales) {
      return arr.sort((a, b) => (b.actualSales?.actualProfit || -Infinity) - (a.actualSales?.actualProfit || -Infinity));
    }
    return arr.sort((a, b) => b.profit - a.profit);
  }, [evaluable, sortMode, hasActualSales]);

  const top5 = sorted.slice(0, 5);
  const bottom3 = sorted.slice(-3).reverse();

  // ── 빈 상태 ──
  if (!menus || menus.length === 0) {
    return (
      <Card>
        <Header title="🍽️ 메뉴 이익률 랭킹" sub="레시피와 판매가를 등록하면 메뉴별 마진이 한눈에 보여요" />
        <EmptyState
          msg="아직 메뉴가 없어요"
          actionLabel="레시피 등록하러 가기"
          onClick={() => onNavigate && onNavigate('recipes')}
        />
      </Card>
    );
  }

  if (evaluable.length === 0) {
    return (
      <Card>
        <Header title="🍽️ 메뉴 이익률 랭킹" sub={`${menus.length}개 메뉴 — 데이터 보강이 필요해요`} />
        <EmptyState
          msg="판매가 + 식재료 단가를 입력하면 마진 분석이 시작돼요"
          hint={`판매가 미입력 ${menus.filter(m => !m.hasPrice).length}개 · 식재료 단가 누락 ${menus.filter(m => m.hasPrice && !m.hasIngredients).length}개`}
          actionLabel="단가표로 이동"
          onClick={() => onNavigate && onNavigate('prices')}
        />
      </Card>
    );
  }

  return (
    <Card>
      <Header title="🍽️ 메뉴 이익률 랭킹" sub={`${evaluable.length}개 분석 · 평균 마진율 ${summary.avgMargin}%`} />

      {/* 정렬 토글 */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <SortBtn active={sortMode === 'margin'} onClick={() => setSortMode('margin')}>마진율 순</SortBtn>
        <SortBtn active={sortMode === 'profit'} onClick={() => setSortMode('profit')}>1개당 이익 순</SortBtn>
        {hasActualSales && (
          <SortBtn active={sortMode === 'actual'} onClick={() => setSortMode('actual')}>
            💡 실제 이익 순
          </SortBtn>
        )}
        {summary.lossCount > 0 && (
          <span style={{
            marginLeft: 'auto', background: '#F4F4F5', color: '#52525B',
            padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
          }}>
            ⚠️ 손해 메뉴 {summary.lossCount}개
          </span>
        )}
      </div>

      {/* 실제 판매 데이터 알림 */}
      {hasActualSales && (
        <div style={{
          padding: '7px 10px', background: '#FAFAFA',
          borderLeft: '3px solid #FF6F0F',
          borderRadius: 6, fontSize: 11, color: '#9A3412',
          marginBottom: 10,
        }}>
          ✅ 이번달 메뉴별 판매 데이터 반영 — 실제 N개 판매 기준 이익 계산
        </div>
      )}
      {!hasActualSales && evaluable.length > 0 && (
        <div style={{
          padding: '7px 10px', background: '#FAFAFA',
          borderLeft: '3px solid #EA580C',
          borderRadius: 6, fontSize: 11, color: '#78350f',
          marginBottom: 10,
        }}>
          💡 매출 입력 시 <strong>메뉴별 입력 모드</strong>로 등록하면 실제 판매 기반 이익 랭킹이 표시돼요
        </div>
      )}

      {/* Top 5 */}
      <SectionLabel color="#18181B">효자 메뉴 Top {Math.min(5, top5.length)}</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {top5.map((m, i) => <MenuRow key={m.id} rank={i + 1} menu={m} sortMode={sortMode} highlight="top" isMobile={isMobile} />)}
      </div>

      {/* Bottom 3 — 평가 가능한 메뉴가 4개 이상일 때만 */}
      {evaluable.length >= 4 && bottom3.length > 0 && (
        <>
          <SectionLabel color="#71717A">요주의 메뉴 Bottom {Math.min(3, bottom3.length)}</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bottom3.map((m, i) => <MenuRow key={m.id} rank={evaluable.length - i} menu={m} sortMode={sortMode} highlight="bottom" isMobile={isMobile} />)}
          </div>
        </>
      )}

      {/* CTA */}
      {summary.incompleteCount > 0 && (
        <div style={{
          marginTop: 12, padding: '8px 12px', background: '#FAFAFA',
          borderRadius: 8, fontSize: 11, color: '#78350f', textAlign: 'center',
        }}>
          💡 {summary.incompleteCount}개 메뉴는 판매가/단가가 부족해서 분석되지 않았어요
        </div>
      )}
    </Card>
  );
}

// ── 작은 컴포넌트 ──
function Card({ children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: '20px 22px',
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)', marginBottom: 18,
    }}>
      {children}
    </div>
  );
}

function Header({ title, sub }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a', marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{sub}</div>
    </div>
  );
}

function SortBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 12px', borderRadius: 14, border: 'none',
      background: active ? '#FF6F0F' : '#f3f4f6',
      color: active ? '#fff' : '#6b7280',
      fontSize: 12, fontWeight: 700, cursor: 'pointer',
    }}>{children}</button>
  );
}

function SectionLabel({ color, children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 800, color, letterSpacing: '0.04em',
      marginBottom: 7, marginTop: 4,
    }}>{children}</div>
  );
}

function EmptyState({ msg, hint, actionLabel, onClick }) {
  return (
    <div style={{ textAlign: 'center', padding: '28px 14px', color: '#9ca3af' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: hint ? 4 : 12 }}>{msg}</div>
      {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12 }}>{hint}</div>}
      {actionLabel && (
        <button onClick={onClick} style={{
          padding: '7px 18px', borderRadius: 8, border: '1.5px solid #FF6F0F',
          background: '#fff', color: '#18181B',
          fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>{actionLabel} →</button>
      )}
    </div>
  );
}

// ── 메뉴 한 행 ──
// 디자인 정책: 오렌지 바·과한 색상 제거. 숫자 중심 미니멀.
//   - 마진율 음수일 때만 #52525B로 톤다운 (양수=다크), 그 외 모두 단색 그레이/다크
//   - 랭크 1위에만 오렌지 액센트 (rank=1 && highlight='top')
function MenuRow({ rank, menu, sortMode, highlight, isMobile }) {
  const isTop = highlight === 'top';
  const isBottom = highlight === 'bottom';
  const isFirstTop = isTop && rank === 1;
  const isNegative = menu.marginPct < 0;

  // 색상 — 1위만 오렌지, 그 외엔 모두 다크/그레이
  const accent = isFirstTop ? '#FF6F0F' : '#3F3F46';
  const pctColor = isNegative ? '#52525B' : '#18181B';
  const profitColor = isNegative ? '#52525B' : '#18181B';

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #F4F4F5',
      borderRadius: 10,
      padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      {/* 1행: 랭크 + 이름 + 마진율 % */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 22, height: 22, flexShrink: 0,
          borderRadius: '50%',
          background: isFirstTop ? accent : '#F4F4F5',
          color: isFirstTop ? '#fff' : '#52525B',
          fontSize: 11, fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{rank}</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#18181B',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {menu.name}
        </span>
        <span style={{ fontSize: 14, fontWeight: 900, color: pctColor, flexShrink: 0 }}>
          {menu.marginPct >= 0 ? '+' : ''}{menu.marginPct}%
        </span>
      </div>

      {/* 2행: 숫자 디테일 (게이지 바 제거됨) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#71717A' }}>
        <span>판매가 <strong style={{ color: '#18181B', marginLeft: 2 }}>{fmt(menu.sellingPrice)}원</strong></span>
        <span>원가 <strong style={{ color: '#18181B', marginLeft: 2 }}>{fmt(menu.costPerUnit)}원</strong></span>
        <span>1개당 <strong style={{ color: profitColor, marginLeft: 2 }}>
          {menu.profit >= 0 ? '+' : ''}{fmt(menu.profit)}원
        </strong></span>
      </div>

      {/* 3행: 실제 판매 데이터 (있을 때만) */}
      {menu.actualSales && menu.actualSales.qty > 0 && (
        <div style={{
          marginTop: 4, padding: '7px 10px',
          background: '#FAFAFA',
          borderRadius: 6,
          display: 'flex', justifyContent: 'space-between',
          fontSize: 11,
        }}>
          <span style={{ color: '#52525B', fontWeight: 700 }}>
            이번달 {menu.actualSales.qty}개 판매
          </span>
          <span style={{ color: '#71717A' }}>
            매출 <strong style={{ color: '#18181B', marginLeft: 2 }}>{fmt(menu.actualSales.revenue)}원</strong>
          </span>
          <span style={{ color: '#71717A' }}>
            총 이익 <strong style={{ color: profitColor, marginLeft: 2 }}>
              {menu.actualSales.actualProfit >= 0 ? '+' : ''}{fmt(menu.actualSales.actualProfit)}원
            </strong>
          </span>
        </div>
      )}

      {/* 데이터 품질 경고 */}
      {menu.dataQuality < 1 && menu.missingIngredients.length > 0 && (
        <div style={{
          fontSize: 10, color: '#71717A', background: '#FAFAFA',
          padding: '3px 8px', borderRadius: 4, marginTop: 2,
        }}>
          단가 미등록: {menu.missingIngredients.slice(0, 3).join(', ')}
          {menu.missingIngredients.length > 3 && ` 외 ${menu.missingIngredients.length - 3}개`}
        </div>
      )}
    </div>
  );
}
